import React, { useEffect, useState } from "react";
import { TextField, InputAdornment } from "@material-ui/core";
import { PrimaryButton } from "@common";
import locale from "@bonliva-crm/locale";
import { useNavigation } from "@navigation";

export type Props = {
  id: string;
  initialAmount: number | null;
  initialDescription: string;
  onSave: (input: { amount: number | null; description: string }) => void;
  isLoading: boolean;
};

const formatAmount = (amount: number | null): string =>
  amount === null || amount === undefined ? "" : String(amount);

export function AdditionalPayoutInput(props: Props) {
  const nav = useNavigation();

  const [amount, setAmount] = useState<string>(formatAmount(props.initialAmount));
  const [description, setDescription] = useState<string>(props.initialDescription);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setAmount(formatAmount(props.initialAmount));
    setDescription(props.initialDescription);
  }, [props.initialAmount, props.initialDescription]);

  useEffect(() => {
    const isDirty =
      amount !== formatAmount(props.initialAmount) ||
      description !== props.initialDescription;
    setDirty(isDirty);
    nav.lockNavigation(
      `additional_payout_${props.id}`,
      isDirty,
      locale.NoteUnSaveBlockMessage
    );
  }, [amount, description, props.initialAmount, props.initialDescription]);

  const handleSave = () => {
    const trimmed = amount.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    if (parsed !== null && Number.isNaN(parsed)) {
      return;
    }
    props.onSave({ amount: parsed, description });
  };

  return (
    <div style={{ display: "flex", flexDirection: "row", gap: 16, width: "100%", alignItems: "flex-start" }}>
      <TextField
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        label={locale.AdditionalPayoutAmount}
        inputProps={{ step: "0.01" }}
        style={{ flex: "0 0 240px" }}
      />
      <TextField
        fullWidth
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        label={locale.AdditionalPayoutDescription}
        style={{ flex: 1 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <PrimaryButton
                disabled={!dirty || props.isLoading}
                style={{ marginBottom: 8 }}
                onClick={handleSave}
                size="small"
              >
                {locale.Save}
              </PrimaryButton>
            </InputAdornment>
          )
        }}
      />
    </div>
  );
}
